/**
 * Korean locale for messaging-ui.
 * Export is the "messaging" subtree for host i18n merge.
 */
import type { MessagingLocale } from './en'

export const messagingKo = {
  list: {
    loading: `메시지 그룹 로딩 중...`,
    error: `오류`,
    empty: `메시지 그룹을 찾을 수 없습니다`,
    emptyDescription: `이 PBX에 대해 구성된 메시지 그룹이 없습니다.`,
    deleteConfirmTitle: `메시지 그룹 삭제`,
    deleteConfirm: `"{{name}}"을(를) 삭제하시겠습니까?`,
    smsEnabled: `사용`,
    smsDisabled: `사용 안 함`,
    delete: `삭제`,
    columnGroupName: `그룹 이름`,
    columnPrimarySms: `기본 SMS`,
    columnMembers: `구성원`,
    columnSmsStatus: `SMS 상태`,
    columnCreated: `생성일`,
    columnUpdated: `수정일`,
    columnActions: `작업`,
    toast: {
      createdTitle: `메시지 그룹이 생성되었습니다`,
      createdDescription: `"{{name}}"이(가) 성공적으로 생성되었습니다.`,
      createFailedTitle: `생성 실패`,
      createFailedDescription: `메시지 그룹을 생성할 수 없습니다`,
      deletedTitle: `메시지 그룹이 삭제되었습니다`,
      deletedDescription: `"{{name}}"이(가) 삭제되었습니다.`,
      deleteFailedTitle: `삭제 실패`,
      deleteFailedDescription: `메시지 그룹을 삭제할 수 없습니다`,
    },
    title: `메시징`,
    createButton: `+ 그룹 만들기`,
    searchPlaceholder: `이름 또는 전화번호로 검색...`,
    noResults: `결과 없음`,
    noResultsDescription: `"{{search}}"와(과) 일치하는 메시지 그룹이 없습니다.`,
    noMembersWarning: `이 그룹에 구성원이 없습니다. 고객 메시지가 읽히지 않을 수 있습니다.`,
  },
  detail: {
    validationErrorTitle: `유효성 검사 오류`,
    validationErrorDescription: `그룹 이름을 입력해 주세요`,
    toast: {
      updatedTitle: `메시지 그룹이 업데이트되었습니다`,
      updateFailedTitle: `업데이트 실패`,
      updateFailedDescription: `메시지 그룹을 업데이트할 수 없습니다`,
      smsAddedTitle: `SMS 번호가 추가되었습니다`,
      smsAddFailedTitle: `SMS 번호를 추가할 수 없습니다`,
      smsRemovedTitle: `SMS 번호가 제거되었습니다`,
      smsRemoveFailedTitle: `SMS 번호를 제거할 수 없습니다`,
      memberAddedTitle: `구성원이 추가되었습니다`,
      memberAddFailedTitle: `구성원을 추가할 수 없습니다`,
      memberAddFailedDescription: `그룹에 구성원을 추가할 수 없습니다`,
      memberRemovedTitle: `구성원이 제거되었습니다`,
      memberRemoveFailedTitle: `구성원을 제거할 수 없습니다`,
    },
    loading: `메시지 그룹 로딩 중...`,
    error: `오류`,
    notFound: `메시지 그룹을 찾을 수 없습니다.`,
    title: `메시지 그룹 편집`,
    viewTitle: `메시지 그룹`,
    groupNameLabel: `그룹 이름`,
    groupNamePlaceholder: `그룹 이름 입력`,
    primarySmsLabel: `기본 SMS 번호`,
    primarySmsPlaceholder: `기본 SMS 번호 선택`,
    campaignIdWarning: `선택한 번호에 캠페인 ID가 없어 SMS를 보내지 못할 수 있습니다. 단, 수신자 부담 서비스 또는 캐나다 번호인 경우는 예외입니다.`,
    associatedSmsLabel: `보조 SMS 번호`,
    noCampaignId: `(캠페인 ID 없음)`,
    removeSmsAria: `SMS 번호 제거`,
    addSmsNumberAria: `SMS 번호 추가`,
    unsavedChanges: `저장되지 않은 변경 사항`,
    campaignSummaryTitle: `캠페인 ID가 없는 번호 {{count}}개`,
    campaignSummaryDescription: `이 번호들은 수신자 부담 서비스 또는 캐나다 번호가 아닌 경우 SMS를 보내지 못할 수 있습니다.`,
    backToList: `← 목록으로 돌아가기`,
    updating: `업데이트 중...`,
    updateButton: `메시지 그룹 업데이트`,
    noMembersWarning: `이 그룹에 구성원이 없습니다. 고객 메시지가 읽히지 않을 수 있습니다.`,
  },
  createModal: {
    validationErrorTitle: `유효성 검사 오류`,
    validationErrorDescription: `메시지 그룹 이름을 입력해 주세요`,
    title: `메시지 그룹 만들기`,
    nameLabel: `그룹 이름`,
    namePlaceholder: `그룹 이름 입력`,
    primarySmsLabel: `기본 SMS 번호`,
    primarySmsPlaceholder: `번호 선택`,
    primarySmsRequired: `기본 SMS 번호를 선택해 주세요`,
    cancel: `취소`,
    creating: `생성 중...`,
    createButton: `그룹 만들기`,
  },
  members: {
    groupMembersTitle: `그룹 구성원`,
    emptyGroup: `이 그룹에 아직 구성원이 없습니다.`,
    columnName: `이름`,
    columnExtension: `내선`,
    columnAdded: `추가됨`,
    availableMembersTitle: `추가 가능한 구성원`,
    emptyAvailable: `추가할 수 있는 구성원이 없습니다.`,
    removeMemberAria: `구성원 제거`,
    addMemberAria: `구성원 추가`,
    searchPlaceholder: `이름 또는 내선으로 검색...`,
    noSearchResults: `검색 결과와 일치하는 구성원이 없습니다.`,
  },
  smsModal: {
    validationErrorTitle: `유효성 검사 오류`,
    validationErrorDescription: `SMS 번호를 선택해 주세요`,
    title: `연결된 SMS 번호`,
    label: `연결된 SMS 번호`,
    loading: `로딩 중...`,
    selectPlaceholder: `번호 선택`,
    submitting: `제출 중...`,
    submit: `제출`,
    close: `닫기`,
  },
  pagination: {
    resultsPerPage: `페이지당 결과:`,
    previousPage: `이전 페이지`,
    nextPage: `다음 페이지`,
    jumpToPage: `{{page}}페이지로 이동`,
    all: `전체`,
  },
} satisfies MessagingLocale
